<script setup lang="ts">
/**
 * 遊戲載入「軟逾時」對話框 —— 載入停滯太久時跳出，讓玩家選「刷新」或「等待」。
 *
 * 🛑 **軟逾時的意思是永不中斷**：逾時只是多給一個逃生門，玩家選「等待」就繼續原本的載入流程。
 *   刻意不做硬逾時（時間到就切錯誤頁）—— 遊戲包在慢速網路本來就可能載好幾分鐘，
 *   任何絕對時長都會誤殺正常玩家，把還差幾秒就能玩的人踢到錯誤頁。
 *
 * 版面對齊競品：白底黑字、訊息一行 → 分隔線 → 兩顆並排按鈕（中間細分隔）。文案走 `common.slowNetwork.*`
 * （逐字對齊競品中英版）。計時 / 重試邏輯不在這裡，見 `useLoadTimeout.ts`。
 */
import { useI18n } from 'vue-i18n'

defineProps<{ show: boolean }>()
const emit = defineEmits<{ refresh: []; wait: [] }>()

const { t } = useI18n()
</script>

<template>
  <Transition name="slow-net-fade">
    <div v-if="show" class="slow-net-mask">
      <div class="slow-net-card">
        <p class="slow-net-message">{{ t('common.slowNetwork.message') }}</p>
        <div class="slow-net-hr" />
        <div class="slow-net-actions">
          <button type="button" class="slow-net-btn" @click="emit('refresh')">
            {{ t('common.slowNetwork.refresh') }}
          </button>
          <div class="slow-net-vr" />
          <button type="button" class="slow-net-btn" @click="emit('wait')">
            {{ t('common.slowNetwork.wait') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* z-index 要在 LoadingScreen（z-50）之上 —— 這個框本來就是蓋在 loading 畫面上的。 */
.slow-net-mask {
  @apply fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-[3rem];
}
/* 白底黑字（不吃主題色）—— 這個框是系統層提示，跨遊戲一致。 */
.slow-net-card {
  @apply w-full max-w-[25rem] overflow-hidden rounded-xl bg-white text-center text-black shadow-lg;
}
.slow-net-message {
  @apply px-[2rem] py-[2rem] text-[1.4rem] leading-relaxed;
}
.slow-net-hr {
  @apply h-px w-full bg-black/10;
}
.slow-net-actions {
  @apply flex items-stretch;
}
.slow-net-btn {
  @apply flex-1 py-[1rem] text-[1.4rem] text-black transition active:bg-black/5;
}
.slow-net-vr {
  @apply w-px shrink-0 bg-black/10;
}

.slow-net-fade-enter-active,
.slow-net-fade-leave-active {
  transition: opacity 0.2s ease-out;
}
.slow-net-fade-enter-from,
.slow-net-fade-leave-to {
  opacity: 0;
}
</style>
